/**
 * Lógica pura do bloco "Acompanhamento da sua carteira" do dashboard.
 *
 * Classifica propostas por proximidade de vencimento, usando `validaAte` como critério objetivo.
 * NÃO há dado de interação, contato ou follow-up — classificar por data é o máximo que o dado
 * sustenta sem prometer o que não se pode confirmar.
 *
 * As funções recebem `hoje` como parâmetro para permitir teste determinístico, pelo mesmo motivo
 * que `periodoDashboard.ts` — se a suíte depender do relógio, um teste aprovado em agosto falha
 * em setembro sem ninguém ter mudado nada.
 */

// ---------------------------------------------------------------------------
// Tipos
// ---------------------------------------------------------------------------

/** Identificador de cada faixa de urgência, ordenado da mais urgente para a menos. */
type FaixaAcompanhamento = 'vencendoJa' | 'vencendoSemana' | 'vencendoMes' | 'vencidaRecente';

/** Definição estática de cada faixa — imutável e conferível de relance. */
interface DefinicaoFaixa {
  readonly id: FaixaAcompanhamento;
  readonly rotulo: string;
  readonly acaoSugerida: string;
  /** Dias mínimos (inclusive). Negativo para vencidas. */
  readonly diasMin: number;
  /** Dias máximos (inclusive). */
  readonly diasMax: number;
  /** Classe de cor para o selo — vermelho, âmbar, azul ou cinza. */
  readonly cor: 'vermelho' | 'ambar' | 'azul' | 'cinza';
}

/** Item genérico com os campos mínimos necessários para classificação. */
interface ItemClassificavel {
  readonly validaAte: string;
  readonly legado: boolean;
  readonly numero: string;
  readonly cliente: string;
  readonly projeto: string;
  readonly url: string;
  readonly financeiro: { readonly mensalidadeUsd: number } | null;
}

/** Resultado do agrupamento: faixa com seus itens e dias calculados. */
interface GrupoFaixa {
  readonly faixa: DefinicaoFaixa;
  readonly itens: readonly ItemComDias[];
}

/** Item enriquecido com a diferença em dias e descrição textual. */
interface ItemComDias {
  readonly item: ItemClassificavel;
  readonly dias: number;
  readonly descricao: string;
  readonly faixa: FaixaAcompanhamento;
}

// ---------------------------------------------------------------------------
// Tabela de faixas
// ---------------------------------------------------------------------------

/**
 * Faixas de urgência, ordenadas da mais urgente para a menos.
 *
 * | Faixa | Critério | Cor | Ação sugerida |
 * | --- | --- | --- | --- |
 * | Vence hoje ou amanhã | 0 ≤ dias ≤ 1 | vermelho | "Fale com o cliente hoje" |
 * | Vence nesta semana | 2 ≤ dias ≤ 7 | âmbar | "Confirme o interesse antes de vencer" |
 * | Vence em até 30 dias | 8 ≤ dias ≤ 30 | azul | "Acompanhe" |
 * | Venceu recentemente | -60 ≤ dias < 0 | cinza | "Ofereça revalidação" |
 *
 * Propostas com mais de 30 dias de folga ou vencidas há mais de 60 dias ficam fora — não exigem
 * ação e só poluiriam a interface.
 */
const FAIXAS: readonly DefinicaoFaixa[] = [
  {
    id: 'vencendoJa',
    rotulo: 'Vence hoje ou amanhã',
    acaoSugerida: 'Fale com o cliente hoje',
    diasMin: 0,
    diasMax: 1,
    cor: 'vermelho',
  },
  {
    id: 'vencendoSemana',
    rotulo: 'Vence nesta semana',
    acaoSugerida: 'Confirme o interesse antes de vencer',
    diasMin: 2,
    diasMax: 7,
    cor: 'ambar',
  },
  {
    id: 'vencendoMes',
    rotulo: 'Vence em até 30 dias',
    acaoSugerida: 'Acompanhe',
    diasMin: 8,
    diasMax: 30,
    cor: 'azul',
  },
  {
    id: 'vencidaRecente',
    rotulo: 'Venceu recentemente',
    acaoSugerida: 'Ofereça revalidação',
    diasMin: -60,
    diasMax: -1,
    cor: 'cinza',
  },
] as const;

// ---------------------------------------------------------------------------
// Cálculos de data
// ---------------------------------------------------------------------------

/**
 * Diferença em dias inteiros entre hoje e a data de validade.
 *
 * Positivo = falta para vencer. Negativo = já venceu.
 *
 * ## Por que não usar `new Date('AAAA-MM-DD')`
 *
 * `new Date('2026-08-14')` interpreta como meia-noite UTC. No fuso de Brasília (UTC-3), isso
 * corresponde a 21h do dia 13. Se hoje for 14/08 local e a proposta vence em 14/08, o cálculo
 * erraria o dia e diria que vence amanhã. Construir com `new Date(ano, mes-1, dia)` usa o fuso
 * local e corresponde ao calendário do vendedor.
 */
function diasAte(validaAte: string, hoje: Date): number {
  const partes = validaAte.split('-');
  const ano = parseInt(partes[0] ?? '0', 10);
  const mes = parseInt(partes[1] ?? '1', 10);
  const dia = parseInt(partes[2] ?? '1', 10);

  // Constrói a data de validade no fuso local, meia-noite
  const validade = new Date(ano, mes - 1, dia);

  // Normaliza hoje para meia-noite local, eliminando horas/minutos/segundos
  const hojeNormalizado = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate());

  // Diferença em milissegundos, convertida para dias inteiros
  const diffMs = validade.getTime() - hojeNormalizado.getTime();
  return Math.round(diffMs / 86_400_000);
}

/**
 * Classifica uma proposta em uma faixa de urgência, ou `null` se não exige ação.
 *
 * Propostas com `validaAte` vazio ou inválido ficam fora — melhor não exibir nada do que exibir
 * uma faixa incorreta que induziria o vendedor a agir sobre informação falsa.
 */
function classificar(validaAte: string, hoje: Date): FaixaAcompanhamento | null {
  if (validaAte === '' || validaAte.length < 10) return null;

  const dias = diasAte(validaAte, hoje);

  for (const faixa of FAIXAS) {
    if (dias >= faixa.diasMin && dias <= faixa.diasMax) {
      return faixa.id;
    }
  }

  return null;
}

/**
 * Descreve o prazo em linguagem natural, por extenso.
 *
 * - `0` → "vence hoje"
 * - `1` → "vence amanhã"
 * - `2..30` → "vence em N dias"
 * - `-1` → "venceu ontem"
 * - `-2..-60` → "venceu há N dias"
 *
 * Singular e plural estão corretos: "vence em 1 dia" nunca aparece porque 1 é "vence amanhã".
 */
function descreverPrazo(dias: number): string {
  if (dias === 0) return 'vence hoje';
  if (dias === 1) return 'vence amanhã';
  if (dias > 1) return `vence em ${String(dias)} dias`;
  if (dias === -1) return 'venceu ontem';
  // dias < -1
  return `venceu há ${String(Math.abs(dias))} dias`;
}

// ---------------------------------------------------------------------------
// Agrupamento
// ---------------------------------------------------------------------------

/**
 * Agrupa itens por faixa de urgência, já ordenados por dias (mais urgente primeiro).
 *
 * Propostas que não se encaixam em nenhuma faixa são descartadas — não exigem ação do vendedor.
 * Dentro de cada faixa, a ordenação é do mais urgente (dias menores/mais negativos) para o menos.
 *
 * Para a faixa de vencidas recentes (dias negativos), "mais urgente" significa a que venceu mais
 * recentemente (dias mais próximo de zero), porque é a que tem maior chance de revalidação.
 */
function agruparAcompanhamento(
  itens: readonly ItemClassificavel[],
  hoje: Date,
): readonly GrupoFaixa[] {
  // Mapeia cada faixa para seus itens classificados
  const mapaFaixas = new Map<FaixaAcompanhamento, ItemComDias[]>();

  for (const faixa of FAIXAS) {
    mapaFaixas.set(faixa.id, []);
  }

  for (const item of itens) {
    const faixaId = classificar(item.validaAte, hoje);
    if (faixaId === null) continue;

    const dias = diasAte(item.validaAte, hoje);
    const entrada: ItemComDias = {
      item,
      dias,
      descricao: descreverPrazo(dias),
      faixa: faixaId,
    };

    const lista = mapaFaixas.get(faixaId);
    if (lista !== undefined) {
      lista.push(entrada);
    }
  }

  // Ordena dentro de cada faixa: mais urgente primeiro
  // Para faixas positivas (vencendo): menor dias = mais urgente
  // Para faixa negativa (vencidas): dias mais próximo de zero = venceu mais recentemente
  for (const [faixaId, lista] of mapaFaixas) {
    if (faixaId === 'vencidaRecente') {
      // Mais recentemente vencida primeiro (dias mais próximo de 0, ou seja, maior valor negativo)
      lista.sort((a, b) => b.dias - a.dias);
    } else {
      // Menos dias restantes primeiro
      lista.sort((a, b) => a.dias - b.dias);
    }
  }

  // Monta resultado na ordem fixa das faixas
  const resultado: GrupoFaixa[] = [];
  for (const faixa of FAIXAS) {
    const itensDoGrupo = mapaFaixas.get(faixa.id) ?? [];
    resultado.push({ faixa, itens: itensDoGrupo });
  }

  return resultado;
}

// ---------------------------------------------------------------------------
// Exports
// ---------------------------------------------------------------------------

export { agruparAcompanhamento, classificar, descreverPrazo, diasAte, FAIXAS };

export type { DefinicaoFaixa, FaixaAcompanhamento, GrupoFaixa, ItemClassificavel, ItemComDias };
